import React from 'react';
import {
  AlertBanner,
  InputTextArea,
  SparkIcon,
  Text,
  TooltipTrigger,
  TruncatedText,
  useReadOnly,
} from '@spark';
import { DateTimeWithTimeTooltip, getLanguageName } from '@/shared/utils/helpers';
import {
  formatConstraintForDisplay,
  pillarTitle,
} from '@/features/tasks/components/CreationWizard/businessGoals/constraintOptions';
import EvaluationNormalizationSummary from './EvaluationNormalizationSummary';
import ModelDetailsCodeSection from './ModelDetailsCodeSection';
import ModelDetailsStudentSetupSection from './ModelDetailsStudentSetupSection';
import ModelDetailsTeacherSetupSection from './ModelDetailsTeacherSetupSection';
import ActivityLogTimeline from './ActivityLogTimeline';

function MetaValue({ children }) {
  return (
    <div
      style={{
        minWidth: 0,
        textAlign: 'right',
        color: 'var(--semantic-content-secondary)',
      }}
    >
      {children}
    </div>
  );
}

function MetaRow({ label, children, nested = false }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 'var(--dimension-spacing-08)',
        width: '100%',
        minHeight: 22,
        paddingLeft: nested ? 'var(--dimension-spacing-16)' : undefined,
      }}
    >
      <Text
        variant={nested ? 'base-regular' : 'base-semibold'}
        color={nested ? 'secondary' : 'primary'}
        as="span"
        style={{ flexShrink: 0 }}
      >
        {label}
      </Text>
      <MetaValue>{children}</MetaValue>
    </div>
  );
}

function displayOrDash(value) {
  if (value == null) return '—';
  const asString = typeof value === 'string' ? value : String(value);
  return asString.trim().length > 0 ? asString : '—';
}

const IMPROVEMENT_METHOD_LABELS = {
  ai: 'AI Annotation',
  human: 'Human Annotation',
};

function formatImprovementMethods(methods) {
  const list = Array.isArray(methods) ? methods : [];
  const named = list.map((method) => IMPROVEMENT_METHOD_LABELS[method]).filter(Boolean);
  return named.length > 0 ? named.join(' and ') : null;
}

function schemaAsText(schemaUsed) {
  if (schemaUsed == null || schemaUsed === '') return '';
  if (typeof schemaUsed === 'string') return schemaUsed;
  try {
    return JSON.stringify(schemaUsed, null, 2);
  } catch {
    return String(schemaUsed);
  }
}

/**
 * Versions → Summary body: prompt boxes, two-column metadata, notes, and
 * the activity log. Matches Figma Task Management 3001:71735.
 */
export default function VersionSummarySection({
  showSubObjectiveInMeta,
  version,
  headerMeta,
  summaryMeta,
  showGenerativeSetup,
  showStudentSetup,
  distillationEvent,
  systemPromptUsed,
  promptUsed,
  schemaUsed,
  modelNotes,
  onModelNotesChange,
  notesSaving,
  notesError,
  isNotesDirty,
  role,
}) {
  const { readOnly } = useReadOnly();
  const languagesDisplay =
    Array.isArray(summaryMeta.taskLanguages) && summaryMeta.taskLanguages.length > 0
      ? summaryMeta.taskLanguages.map((code) => getLanguageName(code)).filter(Boolean).join(', ')
      : '';
  const prioritiesDisplay =
    summaryMeta.businessGoalPriorities.length > 0
      ? summaryMeta.businessGoalPriorities.map((pillarId) => pillarTitle(pillarId)).join(' → ')
      : '';
  const schemaText = schemaAsText(schemaUsed);
  const hasOriginMatchKeys = Boolean(
    distillationEvent?.origin_labels_match_keys
    && Object.keys(distillationEvent.origin_labels_match_keys).length > 0,
  );
  // New AI Improve & Retrain runs set trained_on_label_agreements=false and still
  // store origin keys for export. Only treat missing flags with origin keys as
  // the legacy agreement-trained path.
  const trainedOnLabelAgreements = version?.trained_on_label_agreements === true
    || (version?.trained_on_label_agreements == null && hasOriginMatchKeys);
  const improvementMethodLabel = formatImprovementMethods(distillationEvent?.improvement_methods);
  const usesAiImprovement = Array.isArray(distillationEvent?.improvement_methods)
    && distillationEvent.improvement_methods.includes('ai');
  const matchingRecords = distillationEvent?.origin_labels_match_count;
  const matchingRecordsTotal = distillationEvent?.origin_labels_total_count;
  const matchingRecordsDisplay =
    matchingRecords == null
      ? '—'
      : matchingRecordsTotal > 0
        ? `${matchingRecords} out of ${matchingRecordsTotal}`
        : String(matchingRecords);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        width: '100%',
      }}
    >
      <Text variant="heading-3-semibold" as="h3" style={{ margin: 0 }}>
        Version Summary
      </Text>

      {trainedOnLabelAgreements ? (
        <Text variant="detail-regular" color="secondary">
          This version trained on examples where the AI labels matched the original labels.
        </Text>
      ) : null}

      {showGenerativeSetup ? (
        <>
          <ModelDetailsTeacherSetupSection distillationEvent={distillationEvent} />
          {showStudentSetup && (
            <ModelDetailsStudentSetupSection distillationEvent={distillationEvent} />
          )}
        </>
      ) : (
        <>
        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'stretch',
            width: '100%',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <ModelDetailsCodeSection
              title="System Prompt Used"
              titleVariant="base-semibold"
              text={systemPromptUsed}
              copyText={systemPromptUsed}
              minHeight={120}
              maxHeight={120}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <ModelDetailsCodeSection
              title="Prompt Used"
              titleVariant="base-semibold"
              text={promptUsed}
              copyText={promptUsed}
              minHeight={120}
              maxHeight={120}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <ModelDetailsCodeSection
              title="JSON Schema"
              titleVariant="base-semibold"
              text={schemaText}
              copyText={schemaText}
              minHeight={120}
              maxHeight={120}
            />
          </div>
        </div>
        <EvaluationNormalizationSummary source={distillationEvent} />
        </>
      )}

      <div
        style={{
          display: 'flex',
          gap: 48,
          alignItems: 'flex-start',
          width: '100%',
        }}
      >
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--dimension-spacing-08)',
          }}
        >
          {showSubObjectiveInMeta ? (
            <MetaRow label="Sub-task">
              <TruncatedText text={version.objective_name} maxWidth={220} />
            </MetaRow>
          ) : null}
          <MetaRow label="Task Type">
            {headerMeta.taskTypeTooltip ? (
              <TooltipTrigger text={headerMeta.taskTypeTooltip}>
                <span>{headerMeta.taskTypeDisplay}</span>
              </TooltipTrigger>
            ) : (
              headerMeta.taskTypeDisplay
            )}
          </MetaRow>
          <MetaRow label="Base Model">
            <TruncatedText text={headerMeta.baseModel} maxWidth={220} />
          </MetaRow>
          <MetaRow label="Teacher Model">
            <TruncatedText text={headerMeta.teacherModel} maxWidth={220} />
          </MetaRow>
          <MetaRow label="Last Updated">
            <DateTimeWithTimeTooltip value={headerMeta.lastUpdated} emptyPlaceholder="-" />
          </MetaRow>
          <MetaRow label="Model Size">{summaryMeta.modelSizeDisplay}</MetaRow>
          <MetaRow label="Task Description">
            {displayOrDash(summaryMeta.usecaseGoal)}
          </MetaRow>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--dimension-spacing-04)',
            }}
          >
            <MetaRow label="Improvement method">
              {displayOrDash(improvementMethodLabel)}
            </MetaRow>
            {usesAiImprovement ? (
              <MetaRow
                nested
                label={trainedOnLabelAgreements ? 'Matching records' : 'Training records'}
              >
                {matchingRecordsDisplay}
              </MetaRow>
            ) : null}
          </div>
        </div>

        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--dimension-spacing-08)',
          }}
        >
          <MetaRow label="Task Languages">{displayOrDash(languagesDisplay)}</MetaRow>
          <MetaRow label="Business Goal Priorities">
            {prioritiesDisplay ? (
              <div>
                <div>{prioritiesDisplay}</div>
                <Text variant="detail-regular" color="secondary" as="div">
                  Least important → most important
                </Text>
              </div>
            ) : (
              '—'
            )}
          </MetaRow>
          <MetaRow label="Constraints">
            {summaryMeta.constraints.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: 18, textAlign: 'left' }}>
                {summaryMeta.constraints.map((c, idx) => (
                  <li key={c.id || idx}>{formatConstraintForDisplay(c)}</li>
                ))}
              </ul>
            ) : (
              '—'
            )}
          </MetaRow>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--dimension-spacing-08)',
              marginTop: 4,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Text variant="base-medium" color="primary">
                Model notes
              </Text>
              <TooltipTrigger text="Write any notes about this model for your own reference. Track changes, ideas, or reminders.">
                <span
                  style={{
                    color: 'var(--semantic-content-secondary)',
                    display: 'inline-flex',
                  }}
                >
                  <SparkIcon slug="circle-help" size="small" />
                </span>
              </TooltipTrigger>
              {notesSaving ? (
                <Text variant="base-regular" color="secondary">
                  Saving…
                </Text>
              ) : !isNotesDirty && (modelNotes ?? '').length > 0 ? (
                <Text variant="base-regular" color="secondary">
                  Saved
                </Text>
              ) : null}
            </div>
            {notesError && (
              <AlertBanner
                feedback="error"
                showIcon
                showButton={false}
                showCloseButton={false}
                text={
                  <span>
                    <b>Could not save notes</b>
                    <br />
                    {notesError}
                  </span>
                }
              />
            )}
            <InputTextArea
              showLabel={false}
              showHelperText={false}
              style={{ maxWidth: 'none' }}
              rows={3}
              value={modelNotes}
              onChange={(e) => onModelNotesChange(e.target.value)}
              disabled={readOnly || role === 'Viewer'}
              placeholder="Write any notes about this model for your own reference. Track changes, ideas, or reminders."
            />
            {role === 'Viewer' && (
              <Text variant="base-regular" color="secondary">
                Editor/Admin role required to edit notes.
              </Text>
            )}
          </div>
        </div>
      </div>

      <ActivityLogTimeline
        key={distillationEvent?.id || 'activity-log'}
        entries={summaryMeta.keyChanges}
      />
    </div>
  );
}
